import { useEffect, useState } from "react";
import ExportExcel from "../../components/ExportExcel";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { houseList } from "../../utils";
import { formatDate } from "../../functions";
import { useDispatch, useSelector } from "react-redux";
import { fetchInactiveStudents } from "../../redux/inactiveStudentsSlice";
import FeeLedger from "../fee/feeLedger";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

function InactiveStudents() {
  const dispatch = useDispatch();
  const MySwal = withReactContent(Swal);
  const {
    data: inactiveStudents,
    loading,
    error,
  } = useSelector((state) => state.inactiveStudents);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    dispatch(fetchInactiveStudents());
  }, [dispatch]);

  // ✅ Recall Student Handler
  const handleRecallStudent = async (student) => {
    const admissionNo = student.studentDetails?.admission_no;
    const studentName = student.studentDetails?.name;

    const result = await MySwal.fire({
      title: "Recall Student?",
      text: `Are you sure you want to recall "${studentName}" (${admissionNo})?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Recall!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/RecallOrDeleteDisabledStudents",
          {
            admission_no: admissionNo,
            action: "recall",
          },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success(`${studentName} Recalled`, {
            position: "top-right",
          });
          dispatch(fetchInactiveStudents()); 
        } else {
          toast.error("Recall failed. Please try again.", {
            position: "top-right",
          });
        }
      } catch (error) {
        toast.error("Error occurred while recalling. Try again.", {
          position: "top-right",
        });
      }
    }
  };

  const handleLeftStudent = async (student) => {
    const admissionNo = student.studentDetails?.admission_no;
    const studentName = student.studentDetails?.name;

    const result = await MySwal.fire({
      title: "Mark as Left?",
      text: `Are you sure you want to permanently mark "${studentName}" (${admissionNo}) as left?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Mark as Left!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/RecallOrDeleteDisabledStudents",
          {
            admission_no: admissionNo,
            action: "remove",
          },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success(`${studentName} Marked as Left`, {
            position: "top-right",
          });
          dispatch(fetchInactiveStudents());
        } else {
          toast.error("Action failed. Please try again.", {
            position: "top-right",
          });
        }
      } catch (error) {
        toast.error("Error occurred. Please try again.", {
          position: "top-right",
        });
      }
    }
  };

  const flattenObjectValues = (obj) => {
    let values = [];
    for (let key in obj) {
      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        values = values.concat(flattenObjectValues(value));
      } else if (typeof value === "string") {
        values.push(value);
      }
    }
    return values;
  };

  const filteredRecords = inactiveStudents.filter((student) => {
    const searchLower = searchTerm.toLowerCase();
    const allValues = flattenObjectValues(student);
    return allValues.some((val) => val.toLowerCase().includes(searchLower));
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const getValueByPath = (obj, path) => {
    return path.split(".").reduce((acc, key) => {
      if (!acc) return "";
      const match = key.match(/(.*?)\[(\d+)\]/);
      if (match) {
        const arrayKey = match[1];
        const index = parseInt(match[2], 10);
        return acc[arrayKey] && acc[arrayKey][index] !== undefined
          ? acc[arrayKey][index]
          : "";
      }
      return acc[key] !== undefined ? acc[key] : "";
    }, obj);
  };

  const columnMapping = {
    studentId: "Student ID",
    "studentDetails.admission_no": "Admission No",
    "studentDetails.admissionDate": "Admission Date",
    "studentDetails.name": "Student Name",
    "studentDetails.gender": "Gender",
    "studentDetails.currentClass": "Class",
    "studentDetails.currentSection": "Section",
    "studentDetails.rollNo": "Roll No",
    "studentDetails.house": "House",
    "studentDetails.category": "Category",
    "studentDetails.aadhar_no": "Aadhaar Card No.",
    "studentDetails.dateOfBirth": "Date of Birth",
    "studentDetails.bloodGroup": "Blood Group",
    "studentDetails.handicap": "Handicap",
    "transportFacility.busStop": "Bus Stop",
    "transportFacility.vehicleNo": "Vehicle No",
    "transportFacility.busNumber": "Bus No",
    "parentDetails[0].name": "Father's Name",
    "parentDetails[1].name": "Mother's Name",
  };

  const handleExportExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column mapping is missing or empty.");
      return;
    }

    const formattedData = inactiveStudents.map((item) => {
      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        formattedItem[header] = getValueByPath(item, key);
      });
      return formattedItem;
    });
    ExportExcel(inactiveStudents, `InactiveStudent_${today}`, columnMapping);
  };

  const handleExportClassWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const classGroups = {};

    inactiveStudents.forEach((student) => {
      const cls = student.studentDetails?.currentClass || "Unknown";
      const sec = student.studentDetails?.currentSection || "Unknown";

      if (!classGroups[cls]) {
        classGroups[cls] = {};
      }

      if (!classGroups[cls][sec]) {
        classGroups[cls][sec] = [];
      }

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);

        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }

        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }

        formattedItem[header] = value;
      });

      classGroups[cls][sec].push(formattedItem);
    });

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];
      const sortedSections = Object.keys(sections).sort();

      sortedSections.forEach((sec) => {
        const students = sections[sec];
        students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
        combinedData.push({ "Student ID": `Section ${sec}` });
        combinedData.push(...students);
        combinedData.push({});
      });

      const worksheet = XLSX.utils.json_to_sheet(combinedData, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, className.slice(0, 31));
    });

    XLSX.writeFile(workbook, `InactiveStudents_ClassWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportGenderWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const genderGroups = {};

    inactiveStudents.forEach((student) => {
      const gender = student.studentDetails?.gender || "Unknown";

      if (!genderGroups[gender]) {
        genderGroups[gender] = [];
      }

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);

        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }

        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }

        formattedItem[header] = value;
      });

      genderGroups[gender].push(formattedItem);
    });

    // Create separate sheet for each gender
    Object.entries(genderGroups).forEach(([gender, students]) => {
      students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
      const worksheet = XLSX.utils.json_to_sheet(students, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, gender.slice(0, 31));
    });

    XLSX.writeFile(workbook, `InactiveStudents_GenderWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportAvailingTransportWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();

    const transportStudents = inactiveStudents.filter(
      (student) => !!getValueByPath(student, "transportFacility.busStop"),
    );

    if (transportStudents.length === 0) {
      toast.error("No transport students found");
      return;
    }

    const formattedData = transportStudents.map((student) => {
      const row = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        row[header] = getValueByPath(student, key);
      });
      return row;
    });

    formattedData.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

    XLSX.writeFile(
      workbook,
      `InactiveStudents_AvailingTransport_${today}.xlsx`,
    );
    toast.success("Report Generated");
  };

  const handleExportHouseWiseExcel = () => {
    const workbook = XLSX.utils.book_new();

    houseList.forEach((house) => {
      const studentsInHouse = inactiveStudents.filter(
        (student) => student?.studentDetails?.house === house,
      );

      if (studentsInHouse.length === 0) return;

      const formattedData = studentsInHouse.map((student) => {
        const row = {};
        Object.entries(columnMapping).forEach(([key, header]) => {
          row[header] = getValueByPath(student, key);
        });
        return row;
      });

      // Sort by roll number (as number)
      formattedData.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      XLSX.utils.book_append_sheet(workbook, worksheet, house);
    });

    XLSX.writeFile(workbook, `InactiveStudents_HouseWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((student, index) => (
                          <tr key={index}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Student ID</span> &mdash;{" "}
                              {student.studentId}
                              <br />
                              <span class="text-dark">Admission No</span>{" "}
                              &mdash; {student.studentDetails.admission_no}{" "}
                              <br />
                              <span class="text-dark">Admission Date</span>{" "}
                              &mdash; {student.studentDetails.admissionDate}{" "}
                              <br />
                              <span class="text-dark">Student Name</span>{" "}
                              &mdash; {student.studentDetails.name} <br />
                              <span class="text-dark">Gender</span> &mdash;{" "}
                              {student.studentDetails.gender}
                              <br />
                              <span class="text-dark">Class</span> &mdash;{" "}
                              {student.currentClass} <br />
                              <span class="text-dark">
                                Section
                              </span> &mdash;{" "}
                              {student.studentDetails.currentSection} <br />
                              <span class="text-dark">
                                Roll No
                              </span> &mdash; {student.studentDetails.rollNo}{" "}
                              <br />
                              <span class="text-dark">House</span> &mdash;{" "}
                              {student.studentDetails.house} <br />
                              <span class="text-dark">
                                Category
                              </span> &mdash; {student.studentDetails.category}{" "}
                              <br />
                              <span class="text-dark">
                                Aadhaar Card No.
                              </span>{" "}
                              &mdash; {student.studentDetails.aadhar_no} <br />
                              <span class="text-dark">Date of Birth</span>{" "}
                              &mdash;
                              {student.studentDetails.dateOfBirth}
                              <br />
                              <span class="text-dark">
                                Religion
                              </span> &mdash; {student.studentDetails.religion}
                              <br />
                              <span class="text-dark">
                                Blood Group
                              </span> &mdash;{" "}
                              {student.studentDetails.bloodGroup}
                              <br />
                              <span class="text-dark">
                                Handicap
                              </span> &mdash; {student.studentDetails.handicap}
                              <br />
                              <span class="text-dark">
                                Phone No
                              </span> &mdash; {student.studentDetails.phoneNo}
                              <hr className="w-25" />
                              <span class="text-dark">
                                Stoppage
                              </span> &mdash;{" "}
                              <span className="text-capitalize">
                                {student.transportFacility.busStop || "NONE"}
                              </span>
                              <br />
                              <span class="text-dark">
                                Vehicle No
                              </span> &mdash;{" "}
                              {student.transportFacility.vehicleNo || "N/A"}
                              <br />
                              <span class="text-dark">Bus No</span> &mdash;{" "}
                              {student.transportFacility.busNumber || "N/A"}
                              <br />
                              <br />
                              <span class="text-dark">Inactive Date</span>{" "}
                              &mdash;{" "}
                              <span className="text-uppercase">
                                {new Date(student.deactivatedAt).toLocaleString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  },
                                )}
                              </span>
                              <br />
                              <br />
                              <div className="bg-light rounded-4 px-4 py-2">
                                <FeeLedger
                                  feeDetails={student.feeDetails}
                                  button={false}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span
                                  onClick={() => handleRecallStudent(student)}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Recall Student
                                </span>
                                <span
                                  onClick={() => handleLeftStudent(student)}
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Left Student
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Student Information</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Inactive Students
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div class="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                        <div class="avatar-group">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span className="avatar avatar-md" key={num}>
                              <img
                                alt={`avatar-${num}`}
                                src={`/assets/images/students/${num}.jpg`}
                                className="rounded-circle"
                              />
                            </span>
                          ))}
                        </div>
                        <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                          {inactiveStudents.length.toLocaleString("en-IN")}
                        </h3>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Inactive Students
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.4 MB Size
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportClassWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class/Section Wise
                                </span>
                                <span
                                  onClick={handleExportGenderWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Gender Wise
                                </span>
                                <span
                                  onClick={
                                    handleExportAvailingTransportWiseExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Availing Bus Facility
                                </span>
                                <span
                                  onClick={handleExportHouseWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  House - Wise Report
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default InactiveStudents;
